import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { canViewModule } from "@/lib/moduleCatalog";
import { ShieldAlert } from "lucide-react";

/**
 * Wraps a route element and redirects the user to Dashboard if they are not
 * permitted to view `moduleKey`. If the user is not yet loaded, renders a
 * lightweight placeholder to avoid flashing the target page.
 */
export default function ModuleGuard({ moduleKey, children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (canViewModule(user, moduleKey)) return children;
  // Denied — show a small explainer with a link home rather than a hard redirect
  // so the user understands *why* they can't see the page.
  return (
    <div className="max-w-md mx-auto text-center py-20" data-testid="module-guard-denied">
      <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
        <ShieldAlert size={22} />
      </div>
      <div className="font-display text-xl font-bold text-gray-900">Access restricted</div>
      <p className="text-sm text-gray-600 mt-2">
        Your account does not have permission to view this module.
        Contact your administrator if you need access.
      </p>
      <a href="/" className="inline-block mt-6 text-sm font-semibold text-emerald-900 hover:underline"
        data-testid="module-guard-home-link">
        ← Back to dashboard
      </a>
    </div>
  );
}
